import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, HeaderButton, MultiSelect } from 'ui-library';

import useBundleDocuments, {
    getProcessOptions,
} from 'pages/DocLibraryPage/hooks/useBundleDocuments';

import TableFilter from 'components/TableFilter/TableFilter';
import TableFilterItem from 'components/TableFilter/TableFilterItem';
import BundledDocumentsOverview from './BundledDocumentsOverview/BundledDocumentsOverview';
import ListOfBundledDocuments from './ListOfBundledDocuments';

import './BundledDocuments.css';

type BundledDocumentsProps = {
    productList: { label: string; id: string }[];
    hasAccessPolicy: (policy: string) => boolean;
    handleOpenUploadPage: () => void;
};

const BundledDocuments = ({ productList, hasAccessPolicy, handleOpenUploadPage }: BundledDocumentsProps) => {
    const { t } = useTranslation();
    const productOptions = productList.map(({ id, label }) => ({ value: id, label }));
    const {
        filteredDocumentList,
        productId,
        processId,
        categoryId,
        openedBundledDocumentId,
        backToOverview,
        handleFilter,
        documentsLoading,
        documentCategoryOptions,
    } = useBundleDocuments({ productOptions });

    return (
        <div className="bundled-documents">
            <TableFilter className="align-center bundled-documents__filter">
                {openedBundledDocumentId ? (
                    <HeaderButton
                        type="icon-arrow-left-long"
                        ariaLabel={t(
                            'applicationDetailsHeader.backToOverview',
                        )}
                        className="Left-button flex gap-8"
                        onClick={backToOverview}
                    >
                        {t('applicationDetailsHeader.backToOverview')}
                    </HeaderButton>
                ) : (
                    <>
                        <div className="filter-section-title">
                            {t(
                                'advisoryDashboard.documents.filterBy',
                            )}
                            :
                        </div>
                        <TableFilterItem>
                            <MultiSelect
                                placeholder={t(
                                    'advisoryDashboard.documents.product',
                                )}
                                options={productOptions}
                                value={productId}
                                onChange={handleFilter('product')}
                                withSelectTool
                            />
                        </TableFilterItem>
                        <TableFilterItem>
                            <MultiSelect
                                placeholder={t(
                                    'advisoryDashboard.documents.process',
                                )}
                                options={getProcessOptions({ t })}
                                value={processId}
                                onChange={handleFilter('process')}
                                withSelectTool
                            />
                        </TableFilterItem>
                        <TableFilterItem>
                            <MultiSelect
                                placeholder={t(
                                    'advisoryDashboard.documents.category',
                                )}
                                options={documentCategoryOptions}
                                value={categoryId}
                                onChange={handleFilter('category')}
                                withSelectTool
                            />
                        </TableFilterItem>
                    </>
                )}

                {hasAccessPolicy('ManageBundleDocuments') ? (
                    <TableFilterItem className="ml-auto">
                        <Button
                            type="secondary"
                            size="small"
                            className="doc-library__upload-btn"
                            onClick={handleOpenUploadPage}
                        >
                            {t(
                                'advisoryDashboard.documents.uploadStaticDocument',
                            )}
                        </Button>
                    </TableFilterItem>
                ) : undefined}
            </TableFilter>

            {!openedBundledDocumentId && (
                <BundledDocumentsOverview
                    documentsLoading={documentsLoading}
                    filteredDocumentList={filteredDocumentList}
                />
            )}

            {openedBundledDocumentId && (
                <ListOfBundledDocuments
                    bundledDocumentId={openedBundledDocumentId}
                    productOptions={productOptions}
                />
            )}
        </div>
    );
};

export default BundledDocuments;
